import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ErrorBoundary, Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';

import { HotelMapModel } from 'models';

import { UitkCard, UitkCardContent, UitkCardLink, UitkCardMedia, UitkCardTextSegment } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';

import { MapDialog } from 'components/infosite/property-details/property-map/map-dialog';

const LinkTracked = ClickTracker(Link);

interface WhatsAroundMapState {
  mapActive: boolean;
}

interface WhatsAroundMapProps extends LocalizedComponentProps {
  analyticsStore?: AnalyticsStore;
  routeStore?: RouteStore;
  src: string;
  rfrr: string;
  mapModel: HotelMapModel;
  ratio?: UitkFigureAspectRatioType;
}

@withLocalization
@inject('analytics', 'routeStore')
@observer
export class WhatsAroundMap extends React.Component<WhatsAroundMapProps, WhatsAroundMapState> {
  constructor(props) {
    super(props);

    this.state = {
      mapActive: false
    };

    this.onMapDialogClose = this.onMapDialogClose.bind(this);
  }

  public shouldComponentUpdate(nextState) {
    return this.state.mapActive !== nextState.mapActive;
  }

  public componentDidMount() {
    this.setState({
      mapActive: true
    });
  }

  public render() {
    const { routeStore, src, rfrr = ':WhatsAroundMap.Open', mapModel, l10n, ratio } = this.props;
    const { mapActive } = this.state;
    const mapIsDisabled = !mapActive ? 'is-disabled' : '';
    if (!(src && mapModel)) {
      return null;
    }
    const queryBase = { pwaDialog: 'map' };
    const mapOpenQuery = routeStore.mergeQueryString(routeStore.search, queryBase);
    const linkTo = {
      pathname: routeStore.pathname,
      search: mapOpenQuery
    };

    const mapThumbnailLinkProps = {
      l10n,
      linkTo,
      mapIsDisabled,
      ratio,
      rfrr,
      src
    };

    return (
      <ErrorBoundary label="hotel.information.whats-around.map">
        <QueryRoute query={queryBase}>
          <Layer id="infosite-map" onDismiss={this.onMapDialogClose}>
            <MapDialog mapModel={mapModel} onDismiss={this.onMapDialogClose} />
          </Layer>
        </QueryRoute>
        <AbacusExperiment name="PWA_Whats_Around_Map_CTA">
          <AbacusControl>
            <MapThumbnailLink {...mapThumbnailLinkProps} />
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <UitkCard withBorder={true}>
              <MapThumbnailLink {...mapThumbnailLinkProps} />
              <MapCtaLabel linkTo={linkTo} />
            </UitkCard>
          </AbacusVariant>
          <AbacusVariant bucket={2}>
            <UitkCard withBorder={true}>
              <MapThumbnailLink {...mapThumbnailLinkProps} />
              <MapCtaLabel linkTo={linkTo} />
            </UitkCard>
          </AbacusVariant>
        </AbacusExperiment>
      </ErrorBoundary>
    );
  }

  private onMapDialogClose() {
    if (typeof this.props.routeStore.goBack === 'function') {
      this.props.routeStore.goBack();
    }
  }
}

export interface MapThumbnailLinkProps {
  l10n: any;
  linkTo: {
    pathname: string;
    search: string;
  };
  mapIsDisabled?: string;
  ratio: UitkFigureAspectRatioType;
  rfrr?: string;
  src: string;
}
export const MapThumbnailLink: React.FunctionComponent<MapThumbnailLinkProps> = ({ l10n, linkTo, mapIsDisabled, ratio, rfrr, src }) => (
  <UitkCardMedia className={`uitk-flex ${mapIsDisabled}`}>
    <UitkFigure ratio={ratio}>
      <UitkImage
        alt={l10n.formatText('hotels.infosite.map.a11yLabel')}
        src={src}
        className="uitk-rounded-border-top uitk-rounded-border-bottom"
      />
    </UitkFigure>
    <UitkCardLink>
      <LinkTracked linkName="Launch dynamic map" rfrr={rfrr} data-stid="whats-around-map-link" to={linkTo}>
        <LocalizedText message="hotels.infosite.map.a11yLabel" />
      </LinkTracked>
    </UitkCardLink>
  </UitkCardMedia>
);
MapThumbnailLink['displayName'] = 'MapThumbnailLink';

export const MapCtaLabel = ({ linkTo }) => (
  <UitkCard>
    <UitkCardContent>
      <UitkCardTextSegment className="uitk-type-300 uitk-type-center uitk-button-old-link">
        <span>
          <LocalizedText message="hotels.searchResults.openMapDialog.label" />
        </span>
      </UitkCardTextSegment>
      <UitkCardLink>
        <LinkTracked linkName="Launch dynamic map" rfrr="WhatsAroundMap.ClickLinkViewMap" data-stid="whats-around-map-cta-link" to={linkTo}>
          <LocalizedText message="hotels.searchResults.openMapDialog.label" />
        </LinkTracked>
      </UitkCardLink>
    </UitkCardContent>
  </UitkCard>
);
